import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { contentAPI, getMediaUrl, settingsAPI } from '../services/api';
import axios from 'axios';
import { Mic, Play, Pause, Volume2, LogOut, CheckCircle, Loader2, Timer, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL !== undefined ? process.env.REACT_APP_BACKEND_URL : 'http://localhost:8001';

export default function ExpressionOraleExercise() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const blocId = searchParams.get('bloc');
  const { theme, toggleTheme } = useTheme();
  
  const [bloc, setBloc] = useState(null);
  const [taches, setTaches] = useState([]);
  const [currentTacheIndex, setCurrentTacheIndex] = useState(0);
  const [totalTimeLeft, setTotalTimeLeft] = useState(12 * 60);
  const [startTime] = useState(Date.now());
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  
  const [status, setStatus] = useState('loading');
  const statusRef = useRef('loading');
  useEffect(() => { statusRef.current = status; }, [status]);

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [prepTimeLeft, setPrepTimeLeft] = useState(0);
  const [taskTimeLeft, setTaskTimeLeft] = useState(0);
  const taskTimeLeftRef = useRef(0);
  useEffect(() => { taskTimeLeftRef.current = taskTimeLeft; }, [taskTimeLeft]);

  const [recordingDurations, setRecordingDurations] = useState({});
  const [hasRecorded, setHasRecorded] = useState({});
  const [audioDataUrls, setAudioDataUrls] = useState({});
  const [volume, setVolume] = useState(0);

  const [chatHistory, setChatHistory] = useState([]);
  const [fullTranscript, setFullTranscript] = useState({});
  const [isProcessingChat, setIsProcessingChat] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const hasStartedIntro = useRef(false);
  const silenceTimerRef = useRef(null);
  const audioContextRef = useRef(null);
  const isAdvancingRef = useRef(false);

  const currentUser = JSON.parse(localStorage.getItem('tcf-current-user') || '{}');
  const userName = currentUser.nom || currentUser.username || 'Candidat';

  useEffect(() => {
    const loadBloc = async () => {
      if (!blocId) { navigate('/modules/expression-orale'); return; }
      try {
        const [foundBloc, siteSettings] = await Promise.all([
          contentAPI.getBloc('expression_orale', blocId),
          settingsAPI.getSiteSettings().catch(() => null)
        ]);
        if (!foundBloc) throw new Error('Bloc non trouvé');

        if (siteSettings) {
          setVoiceEnabled(siteSettings.voice_intro_enabled !== false);
        }

        setBloc(foundBloc);
        const blocTasks = foundBloc.tasks || foundBloc.taches || [];
        setTaches(blocTasks);

        // Calculate total time
        const totalSec = blocTasks.reduce((sum, t) => {
          return sum + (parseInt(t.duree) || 0) * 60 + (parseInt(t.dureeSecondes) || 0);
        }, 0);
        setTotalTimeLeft(totalSec || 12 * 60);

        setStatus('intro');
      } catch (error) { navigate('/modules/expression-orale'); }
    };
    loadBloc();
  }, [blocId, navigate]);

  useEffect(() => {
    if (status === 'intro' && taches.length > 0 && !hasStartedIntro.current) {
      hasStartedIntro.current = true; handleWelcomeSpeech();
    }
  }, [status, taches]);

  useEffect(() => {
    let interval;
    // On ne décompte le temps que si l'IA ne parle pas et ne réfléchit pas
    const isIAPsybusy = isSpeaking || isProcessingChat;
    
    if (!isIAPsybusy && (status.includes('recording') || status === 'preparing_t2' || status.includes('live_chat'))) {
      interval = setInterval(() => {
        if (status === 'preparing_t2') {
          setPrepTimeLeft(prev => { if (prev <= 1) { clearInterval(interval); startLiveConversation(1); return 0; } return prev - 1; });
        } else {
          setTaskTimeLeft(prev => { if (prev <= 1) { clearInterval(interval); stopRecordingAndAdvance(); return 0; } return prev - 1; });
        }
      }, 1000);
    }
    
    const globalTimer = setInterval(() => {
      if (!isIAPsybusy) {
        setTotalTimeLeft(prev => Math.max(0, prev - 1));
      }
    }, 1000);
    
    return () => { clearInterval(interval); clearInterval(globalTimer); };
  }, [status, taches, currentTacheIndex, isSpeaking, isProcessingChat]);

  const stopAllAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; audioRef.current = null; }
    setIsSpeaking(false);
    if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
  };

  const speak = async (text) => {
    if (!voiceEnabled) return; // voice intro disabled — skip silently
    setIsSpeaking(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem('tcf-current-user') || '{}');
      const response = await axios.post(`${API_URL}/api/tts/generate`, {
        text, voice: 'nova', bloc_id: blocId,
        tache_number: taches[currentTacheIndex]?.tache || 0,
        user_id: currentUser.id || null
      });
      const audio = new Audio(getMediaUrl(`${API_URL}${response.data.url}`) || `${API_URL}${response.data.url}`);
      audioRef.current = audio;
      return new Promise((resolve) => {
        audio.onended = () => { setIsSpeaking(false); resolve(); };
        audio.onerror = () => { setIsSpeaking(false); resolve(); };
        audio.play().catch(() => { setIsSpeaking(false); resolve(); });
      });
    } catch (err) { setIsSpeaking(false); return new Promise(resolve => setTimeout(resolve, 1000)); }
  };

  const handleWelcomeSpeech = async () => {
    await speak(`Bienvenue ${userName}. Nous allons commencer par la tâche 1. Elle consiste à vous présenter pendant 2 minutes. Si vous êtes prêt, cliquez sur le bouton.`);
    setStatus('waiting_start_t1');
  };

  const startTask1 = () => { 
    stopAllAudio(); 
    setCurrentTacheIndex(0); 
    const t1 = taches[0];
    const duration = (parseInt(t1?.duree) || 2) * 60 + (parseInt(t1?.dureeSecondes) || 0);
    setTaskTimeLeft(duration); 
    startRecording('recording_t1'); 
  };

  const startLiveConversation = async (index) => {
    playBeep();
    const task = taches[index];
    const duration = (parseInt(task?.duree) || (index === 1 ? 3.5 : 4.5)) * 60 + (parseInt(task?.dureeSecondes) || 0);
    setTaskTimeLeft(duration);
    startRecording(index === 1 ? 'live_chat_t2' : 'live_chat_t3');
  };

  const startTask2Sequence = async () => {
    if (isAdvancingRef.current) return;
    isAdvancingRef.current = true; stopAllAudio();
    setCurrentTacheIndex(1); setStatus('speaking_t2'); setChatHistory([]);
    await speak(`La tâche 2 consiste à poser des questions et je vous réponds. C'est une interaction entre moi et vous. Vous aurez 2 minutes de préparation. Le sujet est : ${taches[1].sujet}.`);
    setPrepTimeLeft(120); setStatus('preparing_t2');
    isAdvancingRef.current = false;
  };

  const startTask3Sequence = async () => {
    if (isAdvancingRef.current || currentTacheIndex === 2) return;
    isAdvancingRef.current = true; 
    stopAllAudio();
    setCurrentTacheIndex(2); 
    setStatus('speaking_t3'); 
    setChatHistory([]);
    
    await speak(`Passons à la tâche 3. Vous devez défendre votre point de vue sur le sujet suivant : ${taches[2].sujet}. Je vous écoute.`);
    
    // Petit délai de sécurité pour éviter l'écho
    setTimeout(() => {
      startLiveConversation(2);
      isAdvancingRef.current = false;
    }, 1000);
  };

  const startRecording = async (newStatus) => {
    try {
      if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder; audioChunksRef.current = [];
      if (newStatus.includes('live_chat')) setupVAD(stream);
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        if (audioChunksRef.current.length === 0) { if (newStatus.includes('live_chat') && taskTimeLeftRef.current > 0) setTimeout(() => startRecording(newStatus), 500); return; }
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onloadend = () => { if (newStatus.includes('live_chat')) handleChatTurn(reader.result, newStatus); else { setAudioDataUrls(prev => ({ ...prev, [currentTacheIndex]: reader.result })); setHasRecorded(prev => ({ ...prev, [currentTacheIndex]: true })); } };
        reader.readAsDataURL(audioBlob);
      };
      mediaRecorder.start(); setStatus(newStatus); if (newStatus === 'recording_t1') playBeep();
    } catch (err) { toast.error('Microphone non accessible'); }
  };

  const setupVAD = (stream) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser(); analyser.fftSize = 2048; source.connect(analyser);
    audioContextRef.current = audioContext;
    const dataArray = new Uint8Array(analyser.fftSize);
    const silenceThreshold = 0.05; const silenceDuration = 1500;
    const checkSilence = () => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return;
      
      // Si l'IA parle, on ignore le flux audio pour le VAD
      if (statusRef.current === 'loading' || isSpeaking) {
        requestAnimationFrame(checkSilence);
        return;
      }

      analyser.getByteTimeDomainData(dataArray);
      let sum = 0; for (let i = 0; i < dataArray.length; i++) { const float = (dataArray[i] - 128) / 128; sum += float * float; }
      const rms = Math.sqrt(sum / dataArray.length); setVolume(rms);
      if (rms < silenceThreshold) { if (!silenceTimerRef.current) silenceTimerRef.current = setTimeout(() => { stopRecording(); }, silenceDuration); }
      else { if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; } }
      if (mediaRecorderRef.current.state === 'recording') requestAnimationFrame(checkSilence);
    };
    requestAnimationFrame(checkSilence);
  };

  const handleChatTurn = async (base64Audio, currentStatus) => {
    setIsProcessingChat(true);
    try {
      if (!taches || !taches[currentTacheIndex]) {
        console.error('Task data not found for index:', currentTacheIndex);
        setIsProcessingChat(false);
        if (taskTimeLeftRef.current > 0) setTimeout(() => startRecording(currentStatus), 1000);
        return;
      }
      const res = await axios.post(`${API_URL}/api/ai/chat`, {
        module: 'expression_orale',
        tache_number: taches[currentTacheIndex].tache,
        sujet: taches[currentTacheIndex].sujet,
        history: chatHistory,
        audio_base64: base64Audio,
        user_id: currentUser.id,
        is_session_start: chatHistory.length === 0  // 1 credit for the whole oral session (taches 2+3)
      });
      
      // Si le statut a changé pendant que l'IA réfléchissait, on ignore la réponse
      if (statusRef.current !== currentStatus) {
        setIsProcessingChat(false);
        return;
      }

      const { user_transcription, ai_response_text, ai_response_audio_url } = res.data;
      
      if (ai_response_text && user_transcription && user_transcription.trim().length > 1) {
        setChatHistory(prev => [...prev, { role: 'user', content: user_transcription }, { role: 'assistant', content: ai_response_text }]);
        setFullTranscript(prev => ({ ...prev, [currentTacheIndex]: (prev[currentTacheIndex] || "") + `\nCandidat: ${user_transcription}\nExaminateur: ${ai_response_text}` }));
        
        if (ai_response_audio_url) {
          stopAllAudio(); // Sécurité : arrêter tout audio précédent
          const audio = new Audio(getMediaUrl(`${API_URL}${ai_response_audio_url}`) || `${API_URL}${ai_response_audio_url}`);
          setIsSpeaking(true);
          audio.onended = () => { 
            setIsSpeaking(false); 
            setIsProcessingChat(false); 
            if (taskTimeLeftRef.current > 0) startRecording(currentStatus); 
          };
          audio.onerror = () => { 
            setIsSpeaking(false); 
            setIsProcessingChat(false); 
            if (taskTimeLeftRef.current > 0) startRecording(currentStatus); 
          };
          audio.play().catch(err => {
            console.error("Audio play error:", err);
            setIsSpeaking(false);
            setIsProcessingChat(false);
            if (taskTimeLeftRef.current > 0) startRecording(currentStatus);
          });
        } else { setIsProcessingChat(false); if (taskTimeLeftRef.current > 0) startRecording(currentStatus); }
      } else { setIsProcessingChat(false); if (taskTimeLeftRef.current > 0) setTimeout(() => startRecording(currentStatus), 500); }
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Nombre d'essais épuisé !", {
          description: "Veuillez contacter votre centre pour ajouter des essais.",
          action: {
            label: "WhatsApp",
            onClick: () => window.open(`https://wa.me/${bloc?.whatsapp || '23057235683'}`, '_blank')
          },
          duration: 10000
        });
        setStatus('finished');
        return;
      }
      setIsProcessingChat(false); if (taskTimeLeftRef.current > 0) setTimeout(() => startRecording(currentStatus), 1000);
    }
  };

  const stopRecording = () => { if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop(); };

  const stopRecordingAndAdvance = () => {
    stopRecording();
    stopAllAudio(); // Sécurité : on arrête tout audio en cours
    if (currentTacheIndex === 0) startTask2Sequence();
    else if (currentTacheIndex === 1) startTask3Sequence();
    else handleFinish();
  };

  const formatTime = (seconds) => { const mins = Math.floor(seconds / 60); const secs = seconds % 60; return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`; };
  const playBeep = () => { try { const audioCtx = new AudioContext(); const osc = audioCtx.createOscillator(); const gain = audioCtx.createGain(); osc.connect(gain); gain.connect(audioCtx.destination); osc.frequency.setValueAtTime(880, audioCtx.currentTime); gain.gain.setValueAtTime(0.1, audioCtx.currentTime); osc.start(); osc.stop(audioCtx.currentTime + 0.3); } catch (e) {} };
  const handleQuit = () => { 
    if (window.confirm('Quitter l\'examen ?')) {
      stopAllAudio();
      stopRecording();
      navigate('/modules/expression-orale'); 
    }
  };
  const handleSkipPrep = () => { setPrepTimeLeft(0); startLiveConversation(1); };

  const handleFinish = () => {
    setStatus('finished'); const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    navigate('/modules/expression-orale/results', { state: { blocName: bloc?.name, blocId, taches, recordingDurations, hasRecorded, audioDataUrls, fullTranscript, timeSpent, completedCount: Object.keys(hasRecorded).length + Object.keys(fullTranscript).length } });
  };

  if (status === 'loading' || !bloc) return <div className="h-screen flex items-center justify-center bg-gray-900"><Loader2 className="animate-spin h-12 w-12 text-orange-500" /></div>;

  return (
    <div className={`h-screen flex flex-col overflow-hidden font-sans ${theme === 'dark' ? 'dark' : ''}`}>
      <div className="flex-1 flex flex-col bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-white transition-colors duration-300">
        <header className="px-4 sm:px-8 py-3 sm:py-4 bg-white/80 dark:bg-gray-900/50 backdrop-blur-md border-b border-gray-200 dark:border-white/10 flex justify-between items-center z-10">
          <div className="flex items-center space-x-3">
            <div className="h-8 w-8 md:h-10 md:w-10 bg-orange-500 rounded-xl flex items-center justify-center shadow-lg">
              <Mic className="h-4 w-4 md:h-6 md:w-6 text-white" />
            </div>
            <div>
              <h1 className="text-sm md:text-lg font-bold tracking-tight text-gray-900 dark:text-white">{bloc.name}</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-widest font-semibold hidden sm:block">TCF Canada</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 sm:space-x-6">
            <div className="flex items-center space-x-2 bg-gray-100 dark:bg-white/5 px-3 py-2 rounded-2xl border border-gray-200 dark:border-white/10">
              <Timer className="h-4 w-4 md:h-5 md:w-5 text-orange-500 dark:text-orange-400" />
              <span className="text-base sm:text-xl font-mono font-bold text-orange-500 dark:text-orange-400">{formatTime(totalTimeLeft)}</span>
            </div>
            <button onClick={handleQuit} className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-xl transition-colors">
              <LogOut className="h-5 w-5 md:h-6 md:w-6 text-gray-400" />
            </button>
          </div>
        </header>
  
        <main className="flex-1 overflow-y-auto p-6 md:p-12 flex flex-col items-center relative">
          <div className="w-full max-w-4xl">
          <div className="flex flex-col items-center justify-center space-y-8 py-6">
            {(status.includes('live_chat') || isProcessingChat || isSpeaking) && (
              <div className="w-full bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm border border-gray-200 dark:border-white/10 rounded-3xl p-6 animate-in fade-in slide-in-from-top-4 shadow-sm">
                <p className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-2">Sujet en cours :</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{taches[currentTacheIndex]?.sujet}</p>
              </div>
            )}

            {status === 'preparing_t2' ? (
              <div className="flex flex-col items-center space-y-6">
                <div className="relative h-48 w-48 flex items-center justify-center"><svg className="absolute inset-0 w-full h-full -rotate-90"><circle cx="96" cy="96" r="80" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-gray-200 dark:text-white/5" /><circle cx="96" cy="96" r="80" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-orange-500" strokeDasharray={502} strokeDashoffset={502 * (1 - prepTimeLeft / 120)} /></svg><span className="text-5xl font-black text-orange-500">{formatTime(prepTimeLeft)}</span></div>
                <p className="text-2xl font-bold text-orange-500 uppercase tracking-widest">Préparation</p>
              </div>
            ) : status.includes('live_chat') || isProcessingChat || isSpeaking ? (
              <div className="flex flex-col items-center space-y-8 w-full max-w-md">
                <div className="flex items-center space-x-3 bg-red-500/10 px-4 py-2 rounded-full border border-red-500/20"><Timer className="h-5 w-5 text-red-500" /><span className="text-2xl font-black text-red-500">{formatTime(taskTimeLeft)}</span></div>
                <div className="relative h-64 w-64 flex items-center justify-center">
                  <svg className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none">
                    <circle cx="128" cy="128" r="110" stroke="currentColor" strokeWidth="4" fill="transparent" className="text-gray-200 dark:text-white/5" />
                    <circle cx="128" cy="128" r="110" stroke="currentColor" strokeWidth="6" fill="transparent" className={`${isSpeaking ? 'text-blue-500' : isProcessingChat ? 'text-orange-500' : 'text-red-500'} transition-all duration-100`} strokeDasharray={690} strokeDashoffset={690 * (1 - Math.min(volume * 10, 1))} style={{ filter: `blur(${volume > 0.02 ? '2px' : '0px'})` }} />
                  </svg>
                  <div className={`absolute inset-0 rounded-full border-2 ${isSpeaking ? 'border-blue-500/30 animate-ping' : isProcessingChat ? 'border-orange-500/30 animate-pulse' : 'border-red-500/30 animate-pulse'}`}></div>
                  <div className={`relative h-48 w-48 rounded-full flex items-center justify-center shadow-2xl transition-all duration-500 ${isSpeaking ? 'bg-blue-600 shadow-blue-600/50 scale-110' : isProcessingChat ? 'bg-orange-600 shadow-orange-600/50 scale-95' : 'bg-red-600 shadow-red-600/50'}`}>
                    {isSpeaking ? <Volume2 className="h-20 w-20 text-white" /> : isProcessingChat ? <Loader2 className="h-20 w-20 text-white animate-spin" /> : <Mic className="h-20 w-20 text-white" />}
                  </div>
                </div>
                <p className={`text-2xl font-black uppercase tracking-widest ${isSpeaking ? 'text-blue-500' : isProcessingChat ? 'text-orange-500' : 'text-red-500'}`}>{isSpeaking ? "L'examinateur répond..." : isProcessingChat ? "Réflexion..." : "L'IA vous écoute..."}</p>
                <p className="text-gray-500 dark:text-gray-400 text-sm italic">{isSpeaking ? "Écoutez attentivement." : isProcessingChat ? "Analyse..." : "Parlez naturellement, l'IA détectera quand vous aurez fini."}</p>
              </div>
            ) : status === 'waiting_start_t1' ? (
              <div className="flex flex-col items-center space-y-6">
                <button onClick={startTask1} className="group relative h-48 w-48 rounded-full bg-orange-500 flex items-center justify-center shadow-2xl shadow-orange-500/40 hover:scale-110 transition-all"><Play className="h-20 w-20 text-white fill-current ml-2" /><div className="absolute inset-0 rounded-full border-4 border-orange-400 animate-ping opacity-20"></div></button>
                <p className="text-2xl font-black uppercase tracking-tighter">Cliquez pour commencer</p>
              </div>
            ) : status === 'recording_t1' ? (
              <div className="flex flex-col items-center space-y-8">
                <div className="relative h-48 w-48 bg-red-600 rounded-full flex flex-col items-center justify-center shadow-2xl shadow-red-600/50 animate-pulse text-white"><Mic className="h-10 w-10 mb-2" /><span className="text-4xl font-black">{formatTime(taskTimeLeft)}</span></div>
                <p className="text-2xl font-black text-red-500 uppercase tracking-widest">Enregistrement...</p>
              </div>
            ) : null}
          </div>
          </div>
        </main>

        {/* ── Sticky bottom nav — always visible at any screen size ── */}
        <div className="flex-shrink-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-200 dark:border-white/10 px-4 py-3 z-20">
          <div className="max-w-4xl mx-auto space-y-2">
            {/* Task tabs */}
            <div className="flex gap-2 bg-gray-100 dark:bg-gray-800/60 p-1.5 rounded-2xl border border-gray-200 dark:border-white/5">
              {[1, 2, 3].map(num => (
                <div key={num} className={`flex-1 flex items-center justify-center py-2.5 rounded-xl transition-all duration-300 ${currentTacheIndex + 1 === num ? 'bg-orange-500 text-white shadow-lg' : 'text-gray-500 dark:text-gray-400'}`}>
                  <span className="text-sm font-bold uppercase tracking-tight">Tâche {num}</span>
                  {(hasRecorded[num-1] || (num > 1 && fullTranscript[num-1])) && <CheckCircle className="ml-1.5 h-3.5 w-3.5" />}
                </div>
              ))}
            </div>
            {/* Context-sensitive action button */}
            {status === 'preparing_t2' && (
              <button onClick={handleSkipPrep} className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-2xl text-sm font-black uppercase tracking-widest transition-all shadow-lg shadow-orange-500/20">
                Commencer maintenant
              </button>
            )}
            {(status.includes('live_chat') || isProcessingChat || isSpeaking) && (
              <button onClick={stopRecordingAndAdvance} className="w-full py-3 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 border border-gray-200 dark:border-white/10 rounded-2xl text-sm font-black uppercase tracking-widest transition-all text-gray-700 dark:text-white">
                Terminer cette tâche
              </button>
            )}
            {status === 'recording_t1' && (
              <button onClick={stopRecordingAndAdvance} className="w-full py-3 bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/20 border border-gray-200 dark:border-white/10 rounded-2xl text-sm font-black uppercase tracking-widest transition-all text-gray-700 dark:text-white">
                Passer cette tâche
              </button>
            )}
          </div>
        </div>

      <footer className="flex-shrink-0 px-4 sm:px-8 py-3 bg-white/80 dark:bg-gray-900/80 border-t border-gray-200 dark:border-white/5 flex justify-between items-center text-[10px] text-gray-500 font-bold uppercase tracking-widest relative z-10">
        <div className="flex items-center space-x-4">
          <p>© TCF AI SYSTEM</p>
          <button
            onClick={toggleTheme}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors ml-4"
            title="Changer le thème"
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4 text-yellow-500" />
            ) : (
              <Moon className="h-4 w-4 text-blue-500" />
            )}
          </button>
        </div>
        <p>UTILISATEUR : {userName}</p>
      </footer>
      </div>
    </div>
  );
}
